import React, { useState, useRef } from 'react';
import { Shield, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import FileUploadSection from './FileUploadSection';
import { inputClass, selectClass, labelClass } from './vaultUtils';

const InsuranceEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    policy_type: entry?.data?.policy_type || '',
    policy_number: entry?.data?.policy_number || '',
    advisor_name: entry?.data?.advisor_name || '',
    advisor_contact: entry?.data?.advisor_contact || '',
    nominee_name: entry?.data?.nominee_name || ''
  });

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name to save as');
      return;
    }
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="insurance-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Shield className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Insurance Policy' : 'Edit Insurance Policy'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles} 
            setPendingFiles={setPendingFiles} 
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          <div className="space-y-2">
            <Label className={labelClass}>Save As *</Label>
            <Input name="save_as" value={formData.save_as} onChange={handleChange} placeholder="e.g., LIC Term Policy" className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Policy Type</Label>
              <select name="policy_type" value={formData.policy_type} onChange={handleChange} className={selectClass}>
                <option value="">Select</option>
                <option value="Life Insurance">Life Insurance</option>
                <option value="Term Insurance">Term Insurance</option>
                <option value="Health Insurance">Health Insurance</option>
                <option value="Vehicle Insurance">Vehicle Insurance</option>
                <option value="Home Insurance">Home Insurance</option>
                <option value="ULIP">ULIP</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Policy Number</Label>
              <Input name="policy_number" value={formData.policy_number} onChange={handleChange} placeholder="Policy number" className={inputClass} />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-white font-medium border-b border-slate-700 pb-2">Advisor Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelClass}>Advisor Name</Label>
                <Input name="advisor_name" value={formData.advisor_name} onChange={handleChange} placeholder="Full name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className={labelClass}>Advisor Contact</Label>
                <Input name="advisor_contact" value={formData.advisor_contact} onChange={handleChange} placeholder="Phone/Email" className={inputClass} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Nominee Name</Label>
            <Input name="nominee_name" value={formData.nominee_name} onChange={handleChange} placeholder="Full name of nominee" className={inputClass} />
          </div>
        </form>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</> : mode === 'add' ? 'Add Entry' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceEntryModal;
